import {
  useCallback, useEffect, useMemo, useRef, useState,
  type FormEvent, type KeyboardEvent, type ReactElement,
} from 'react';
import { AlertTriangle, Eye, EyeOff, Loader2, Lock, LogOut, Shield, Clock } from '../../icons';
import { apiClient } from '../../api/client';
import { verifyUnlockPassword } from '../../api/screen-lock';
import { useAuthStore } from '../../store/authStore';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import {
  useScreenLockStore, subscribeScreenLockSync, logoutDeadline,
  PREWARN_SEC, type LockDesign,
} from '../../store/screenLockStore';
import {
  setSessionExpiryHandler, setSessionIdleMs, resetSessionIdle, useSessionIdle,
  SESSION_IDLE_MS,
} from '../../lib/session-idle';
import { getDateLocale } from '../../lib/base-i18n';
import { tLock } from '../../lib/screen-lock-i18n';
import './ScreenLock.css';

/**
 * ScreenLock (screen-lock — 2026-08-19) — 자리를 비우면 화면을 덮는 가림막.
 *
 * 흐름: 무활동 대기(기본 60분) → 잠금 → 비밀번호로 해제 → 안 풀면 60분 뒤 로그아웃.
 * 무활동 판정은 이미 있는 세션 엔진(`lib/session-idle`)을 그대로 쓴다 — 활동 감지·멀티탭
 * 공유가 이미 그 안에 있고, 지금까지 그 엔진이 하던 "만료=로그아웃" 을 잠금이 가로챈다.
 *
 * 잠금 상태는 localStorage 에 남는다 — 새로고침이나 탭 복제로 풀리면 잠근 뜻이 없다.
 */

/** AppLayout 이 상시 마운트하는 호스트 — 잠기지 않았으면 아무것도 그리지 않는다. */
export function ScreenLockHost(): ReactElement | null {
  const settings = useScreenLockStore((s) => s.settings);
  const lockedAt = useScreenLockStore((s) => s.lockedAt);
  const preview = useScreenLockStore((s) => s.preview);
  const lock = useScreenLockStore((s) => s.lock);
  const hydrate = useScreenLockStore((s) => s.hydrate);

  // 탭 간 동기 + 저장소 재읽기 — 마운트 1회.
  useEffect(() => {
    hydrate();
    return subscribeScreenLockSync();
  }, [hydrate]);

  // 세션 엔진 결선: 켜져 있으면 대기 시간을 넘기고 만료를 가로챈다.
  //   꺼져 있으면 핸들러를 떼고 60분 기본값으로 되돌린다 → 예전(자동 로그아웃) 동작 그대로.
  useEffect(() => {
    if (!settings.enabled) {
      setSessionExpiryHandler(null);
      setSessionIdleMs(SESSION_IDLE_MS);
      return;
    }
    setSessionIdleMs(settings.idleMinutes * 60_000);
    setSessionExpiryHandler(() => {
      useScreenLockStore.getState().lock();
      return true;   // 로그아웃 보류 — 잠금 화면이 그 뒤를 맡는다
    });
    return () => { setSessionExpiryHandler(null); };
  }, [settings.enabled, settings.idleMinutes]);

  // 단축키 Ctrl/⌘+L — 자리를 비우기 전에 직접 잠근다.
  useEffect(() => {
    if (!settings.enabled) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'l' || e.key === 'L')) {
        e.preventDefault();
        lock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settings.enabled, lock]);

  const locked = settings.enabled && lockedAt !== null;

  if (preview !== null) return <LockScreen design={preview} preview />;
  if (!locked) return <PrewarnToast />;
  return <LockScreen design={settings.design} lockedAt={lockedAt} />;
}

/**
 * 잠금 예고 — 잠기기 5분 전에 우측 아래에서 한 번 알린다.
 * 작성 중이던 글을 두고 잠기는 일을 줄이기 위한 것이라, 계속 쓰겠다고 하면 타이머를 되감는다.
 */
function PrewarnToast(): ReactElement | null {
  const settings = useScreenLockStore((s) => s.settings);
  const lock = useScreenLockStore((s) => s.lock);
  const { remaining } = useSessionIdle();
  const [dismissedAt, setDismissedAt] = useState(0);

  const show = settings.enabled && settings.prewarn
    && remaining > 0 && remaining <= PREWARN_SEC
    // 되감은 뒤(남은 시간이 다시 넉넉해진 뒤)에는 다시 뜰 수 있어야 한다.
    && dismissedAt === 0;

  useEffect(() => {
    if (remaining > PREWARN_SEC && dismissedAt !== 0) setDismissedAt(0);
  }, [remaining, dismissedAt]);

  if (!show) return null;

  return (
    <div className="scr-lock-warn" role="status">
      <div className="scr-lock-warn__head">
        <Clock size={15} aria-hidden />
        {tLock('warn.title', { min: Math.max(1, Math.ceil(remaining / 60)) })}
      </div>
      <p className="scr-lock-warn__body">{tLock('warn.body')}</p>
      <div className="scr-lock-warn__actions">
        <button type="button" className="scr-lock-warn__btn" onClick={() => { setDismissedAt(Date.now()); lock(); }}>
          {tLock('warn.lock_now')}
        </button>
        <button
          type="button"
          className="scr-lock-warn__btn scr-lock-warn__btn--pri"
          onClick={() => { resetSessionIdle(); setDismissedAt(Date.now()); }}
        >
          {tLock('warn.keep')}
        </button>
      </div>
    </div>
  );
}

type UnlockError = 'invalid' | 'sso' | 'mfa' | 'error' | null;

/** 가림막 본체 — 디자인 3안은 같은 뼈대에 data-design 만 다르다. */
function LockScreen({ design, lockedAt, preview = false }: {
  design: LockDesign;
  lockedAt?: number | null;
  preview?: boolean;
}): ReactElement {
  const user = useAuthStore((s) => s.user);
  const settings = useScreenLockStore((s) => s.settings);
  const unlock = useScreenLockStore((s) => s.unlock);
  const bumpFail = useScreenLockStore((s) => s.bumpFail);
  const fails = useScreenLockStore((s) => s.fails);
  const setPreview = useScreenLockStore((s) => s.setPreview);

  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UnlockError>(null);
  const [now, setNow] = useState(() => Date.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // 미리보기에는 잠긴 시각이 없다 — 화면이 실제와 같게 보이도록 "지금" 을 기준으로 센다.
  const previewStart = useRef(Date.now());

  useBodyScrollLock(!preview);

  // 1초 시계 — 남은 시간 표시 + 자동 로그아웃 시한 판정.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // 미리보기는 5초 뒤 저절로 닫힌다(설정 화면에서 잠깐 보는 용도).
  useEffect(() => {
    if (!preview) return;
    const t = setTimeout(() => setPreview(null), 5000);
    return () => clearTimeout(t);
  }, [preview, setPreview]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const doLogout = useCallback(() => {
    // 서버 세션까지 정리 — 실패해도 클라이언트는 반드시 정리한다.
    useScreenLockStore.getState().unlock();
    apiClient.post('/auth/logout').catch(() => { /* best-effort */ })
      .finally(() => { useAuthStore.getState().clearSession(); });
  }, []);

  const deadline = useMemo(
    () => logoutDeadline(lockedAt ?? (preview ? previewStart.current : null), settings.logoutMinutes),
    [lockedAt, preview, settings.logoutMinutes],
  );
  const leftSec = deadline === null ? null : Math.max(0, Math.ceil((deadline - now) / 1000));

  // 시한이 다 되면 로그아웃. 미리보기에서는 절대 로그아웃하지 않는다.
  useEffect(() => {
    if (preview || leftSec === null) return;
    if (leftSec <= 0) doLogout();
  }, [preview, leftSec, doLogout]);

  const onSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (preview || busy) return;
    if (!password) { setError('invalid'); return; }
    setBusy(true);
    setError(null);
    const result = await verifyUnlockPassword(password);
    setBusy(false);
    if (result === 'ok') {
      setPassword('');
      resetSessionIdle();       // 해제 = 지금부터 다시 센다
      unlock();
      return;
    }
    setError(result);
    setPassword('');
    if (result === 'invalid') {
      const n = bumpFail();
      // 허용 횟수(기본 3)를 넘기면 로그아웃 — 로그인 화면으로 보낸다.
      if (n >= settings.maxFails) doLogout();
    }
  }, [preview, busy, password, unlock, bumpFail, doLogout, settings.maxFails]);

  // 포커스 가둠 — 잠긴 화면 뒤의 요소로 Tab 이 새 나가지 않게.
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const focusables = cardRef.current?.querySelectorAll<HTMLElement>(
      'input, button, [href], [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables.item(0);
    const last = focusables.item(focusables.length - 1);
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, []);

  const initial = (user?.name ?? '?').trim().slice(0, 1);
  const remainLabel = leftSec === null ? null : formatCountdown(leftSec);
  const urgent = leftSec !== null && leftSec <= 5 * 60;
  const ssoMode = error === 'sso';

  const clock = new Date(now);
  const hh = String(clock.getHours()).padStart(2, '0');
  const mm = String(clock.getMinutes()).padStart(2, '0');

  return (
    <div
      className="scr-lock"
      data-design={design}
      data-preview={preview || undefined}
      role="dialog"
      aria-modal="true"
      aria-label={tLock('a11y.dialog')}
      onKeyDown={onKeyDown}
    >
      {design === 'cover' && (
        <>
          <div className="scr-lock__brand" aria-hidden><span className="scr-lock__brand-sq" />Axi</div>
          <div className="scr-lock__clock">
            <div className="scr-lock__clock-hm">{hh}:{mm}</div>
            <div className="scr-lock__clock-date">
              {clock.toLocaleDateString(getDateLocale(), {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </div>
          </div>
        </>
      )}

      {preview && <div className="scr-lock__preview-tag">{tLock('preview.tag')}</div>}

      <div className="scr-lock__card" ref={cardRef}>
        {design === 'blur' ? (
          <span className={`scr-lock__badge${urgent ? ' scr-lock__badge--warn' : ''}`} aria-hidden>
            {urgent ? <Clock size={21} /> : <Lock size={21} />}
          </span>
        ) : (
          <span className="scr-lock__avatar" aria-hidden>{initial}</span>
        )}

        <p className="scr-lock__title">
          {urgent ? tLock('title.soon') : tLock('title.locked')}
        </p>
        <p className="scr-lock__sub">
          {ssoMode ? tLock('sso.sub') : (user ? `${user.name} · ${user.email}` : tLock('sub.fallback'))}
        </p>

        {ssoMode ? (
          <>
            <button type="button" className="scr-lock__btn" onClick={doLogout}>
              <Shield size={15} aria-hidden />{tLock('sso.relogin')}
            </button>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <div className={`scr-lock__field${error === 'invalid' ? ' scr-lock__field--err' : ''}`}>
              <input
                ref={inputRef}
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={tLock('field.placeholder')}
                aria-label={tLock('field.label')}
                autoComplete="current-password"
                disabled={preview || busy}
              />
              <button
                type="button"
                className="scr-lock__eye"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? tLock('field.hide') : tLock('field.show')}
                tabIndex={-1}
              >
                {show ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            </div>

            {error === 'invalid' && (
              <p className="scr-lock__err" role="alert">
                <AlertTriangle size={14} aria-hidden />
                {tLock('err.invalid', { left: Math.max(0, settings.maxFails - fails) })}
              </p>
            )}
            {(error === 'error' || error === 'mfa') && (
              <p className="scr-lock__err" role="alert">
                <AlertTriangle size={14} aria-hidden />
                {tLock(error === 'mfa' ? 'err.mfa' : 'err.network')}
              </p>
            )}

            <button type="submit" className="scr-lock__btn" disabled={preview || busy}>
              {busy ? <Loader2 size={15} className="scr-lock__spin" aria-hidden /> : <Lock size={15} aria-hidden />}
              {tLock('btn.unlock')}
            </button>
          </form>
        )}

        <div className="scr-lock__foot">
          <span>
            {remainLabel === null
              ? tLock('foot.no_logout')
              : tLock('foot.logout_in')}
            {remainLabel !== null && (
              <b className={urgent ? 'scr-lock__count scr-lock__count--warn' : 'scr-lock__count'}>{remainLabel}</b>
            )}
          </span>
          <button type="button" className="scr-lock__link" onClick={preview ? () => setPreview(null) : doLogout}>
            {preview ? tLock('preview.close') : <><LogOut size={12} aria-hidden />{tLock('foot.logout')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 남은 시간 표기 — 1시간 이상은 h:mm:ss, 그 아래는 mm:ss. */
function formatCountdown(sec: number): string {
  const s = Math.max(0, sec);
  const pad = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
}
